import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CiCircleInfo } from "react-icons/ci";
import { OnestBoldSmall, OnestNormalSmall } from "../../../styled/TextComponents";
import { color_black, color_grey_dark, color_grey_ultra_light, color_white } from "../../../../constants/colors";
import { POPOVER_Z_INDEX } from "../../../../constants/layers";

const POPOVER_WIDTH = 300;
const VIEWPORT_PADDING = 12;
const GAP = 8;

const clamp = (value, min, max) => {
    if (max < min) {
        return min;
    }
    return Math.min(Math.max(value, min), max);
};

const getAnchorPosition = (anchorRect, align, popoverWidth = POPOVER_WIDTH, popoverHeight = 0) => {
    const maxLeft = window.innerWidth - popoverWidth - VIEWPORT_PADDING;
    const left = align === "right"
        ? anchorRect.right - popoverWidth
        : anchorRect.left;

    const maxTop = window.innerHeight - popoverHeight - VIEWPORT_PADDING;
    const initialTop = anchorRect.bottom + GAP;

    return {
        top: clamp(initialTop, VIEWPORT_PADDING, maxTop),
        left: clamp(left, VIEWPORT_PADDING, maxLeft),
    };
};

export const ScheduleInfoHint = ({ title, steps = [], align = "right" }) => {
    const titleId = useId();
    const [isOpen, setIsOpen] = useState(false);
    const [isHoveringAnchor, setIsHoveringAnchor] = useState(false);
    const [isHoveringPopover, setIsHoveringPopover] = useState(false);
    const anchorRef = useRef(null);
    const popoverRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });

    const updatePosition = useCallback(() => {
        const anchor = anchorRef.current;
        const popover = popoverRef.current;
        if (!anchor) {
            return;
        }

        const anchorRect = anchor.getBoundingClientRect();
        const popoverRect = popover?.getBoundingClientRect();
        const popoverWidth = popoverRect?.width || POPOVER_WIDTH;
        const popoverHeight = popoverRect?.height || 0;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const initial = getAnchorPosition(anchorRect, align, popoverWidth, popoverHeight);
        let top = initial.top;
        let left = initial.left;

        if (popoverHeight > 0 && top + popoverHeight > viewportHeight - VIEWPORT_PADDING) {
            const aboveTop = anchorRect.top - popoverHeight - GAP;
            top = aboveTop >= VIEWPORT_PADDING
                ? aboveTop
                : clamp(top, VIEWPORT_PADDING, viewportHeight - popoverHeight - VIEWPORT_PADDING);
        }

        left = clamp(left, VIEWPORT_PADDING, viewportWidth - popoverWidth - VIEWPORT_PADDING);
        setPosition({ top, left });
    }, [align]);

    const openHint = () => {
        const anchorRect = anchorRef.current?.getBoundingClientRect();
        if (anchorRect) {
            const popoverRect = popoverRef.current?.getBoundingClientRect();
            setPosition(
                getAnchorPosition(
                    anchorRect,
                    align,
                    popoverRect?.width || POPOVER_WIDTH,
                    popoverRect?.height || 0,
                ),
            );
        }
        setIsOpen(true);
    };

    const toggleHint = () => {
        if (isOpen) {
            setIsOpen(false);
            return;
        }
        openHint();
    };

    useEffect(() => {
        if (isHoveringAnchor || isHoveringPopover) {
            openHint();
            return;
        }
        setIsOpen(false);
    }, [isHoveringAnchor, isHoveringPopover]);

    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }
        updatePosition();
    }, [isOpen, updatePosition, title, steps]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleReposition = () => updatePosition();
        const handleMouseDown = (event) => {
            if (
                !anchorRef.current?.contains(event.target)
                && !popoverRef.current?.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };

        window.addEventListener("resize", handleReposition);
        window.addEventListener("scroll", handleReposition, true);
        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("resize", handleReposition);
            window.removeEventListener("scroll", handleReposition, true);
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, updatePosition]);

    const popover = isOpen && createPortal(
        <div
            ref={popoverRef}
            role="dialog"
            aria-labelledby={titleId}
            onMouseEnter={() => setIsHoveringPopover(true)}
            onMouseLeave={() => setIsHoveringPopover(false)}
            style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                zIndex: POPOVER_Z_INDEX,
                width: POPOVER_WIDTH,
                maxWidth: `calc(100vw - ${VIEWPORT_PADDING * 2}px)`,
                maxHeight: `calc(100vh - ${VIEWPORT_PADDING * 2}px)`,
                overflowY: "auto",
                padding: "12px 14px",
                borderRadius: 10,
                backgroundColor: color_white,
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.14)",
                boxSizing: "border-box",
            }}
        >
            <OnestBoldSmall id={titleId} style={{ display: "block", marginBottom: 10, color: color_black }}>
                {title}
            </OnestBoldSmall>
            {steps.map((step, index) => (
                <OnestNormalSmall
                    key={index}
                    style={{
                        display: "block",
                        lineHeight: "140%",
                        color: color_black,
                        marginBottom: index < steps.length - 1 ? 8 : 0,
                    }}
                >
                    {step}
                </OnestNormalSmall>
            ))}
        </div>,
        document.body,
    );

    return (
        <>
            <button
                ref={anchorRef}
                type="button"
                className="student-schedule-info-btn"
                aria-label={title}
                aria-expanded={isOpen}
                title={title}
                onClick={toggleHint}
                onMouseEnter={() => setIsHoveringAnchor(true)}
                onMouseLeave={() => setIsHoveringAnchor(false)}
                onFocus={() => setIsHoveringAnchor(true)}
                onBlur={() => setIsHoveringAnchor(false)}
                style={{
                    border: "none",
                    background: color_grey_ultra_light,
                    borderRadius: "50%",
                    width: 22,
                    height: 22,
                    padding: 0,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: color_grey_dark,
                    flexShrink: 0,
                }}
            >
                <CiCircleInfo size={16} />
            </button>
            {popover}
        </>
    );
};
